// Month-string helpers. All operations are in UTC — billing is denominated in
// UTC calendar months and we deliberately don't shift to the customer's local
// timezone (per design doc 013 §1a).

/** First calendar month the credit-pack billing system was live. Hard floor
 * on the MonthPicker — pre-system months would show all zeros and confuse
 * customers. The "vs Apr 2026" comparison card on the May 2026 view is fine
 * to keep — it surfaces pre-system data that customers were already aware of
 * under the old token quota. */
export const CREDITS_START_MONTH = "2026-05";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export const SHORT_MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** Parse "YYYY-MM" → { year, month } (1-12). */
export function parseMonth(s: string): { year: number; month: number } {
  const m = /^(\d{4})-(\d{2})$/.exec(s);
  if (!m) throw new Error(`Invalid month: ${s}`);
  return { year: parseInt(m[1], 10), month: parseInt(m[2], 10) };
}

/** Format YYYY-MM → "May 2026". */
export function formatMonthLong(s: string): string {
  const { year, month } = parseMonth(s);
  return `${MONTH_NAMES[month - 1]} ${year}`;
}

/** Format YYYY-MM → "May 2026" (short month). */
export function formatMonthShort(s: string): string {
  const { year, month } = parseMonth(s);
  return `${SHORT_MONTH_NAMES[month - 1]} ${year}`;
}

/** Current UTC year-month string. */
export function currentUtcMonth(): string {
  const now = new Date();
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth() + 1;
  return `${year}-${String(month).padStart(2, "0")}`;
}

/** Trailing N calendar months ending at the current UTC month, newest first.
 * Returns array of "YYYY-MM" strings. Hard-floored at CREDITS_START_MONTH —
 * earlier months are dropped from the list even if the window would include
 * them. */
export function trailingMonths(n: number): string[] {
  const now = new Date();
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth() + 1;
  const out: string[] = [];
  for (let i = 0; i < n; i++) {
    let m = month - i;
    let y = year;
    while (m <= 0) {
      m += 12;
      y -= 1;
    }
    const candidate = `${y}-${String(m).padStart(2, "0")}`;
    if (candidate < CREDITS_START_MONTH) break;
    out.push(candidate);
  }
  return out;
}

/** True if `month` (YYYY-MM) is on or after the credits system's start month. */
export function isSelectableMonth(month: string): boolean {
  return month >= CREDITS_START_MONTH;
}

/** Number of days in a given UTC month (YYYY-MM). */
export function daysInMonth(s: string): number {
  const { year, month } = parseMonth(s);
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** First-of-the-month after the selected one (for "Resets on" / "by" labels). */
export function firstOfNextMonth(s: string): Date {
  const { year, month } = parseMonth(s);
  return new Date(Date.UTC(year, month, 1));
}
